/**
 * Unified navigation for the Go-backed /app shell.
 *
 * Regrouped per docs/design/ux-overhaul-hld.md §6 "Navigation IA" into nine
 * task-shaped groups (Home, Academics, Students & Admissions, Attendance &
 * Leave, Fees, Operations, CRM & Communication, People & Payroll,
 * Administration) — replacing the legacy Sidebar.tsx-mirrored categories
 * (which mixed 20 unrelated items into "School Management" and carried
 * three separate entries pointing at /app/hr, /app/analytics, /app/leads,
 * and two at /app/calendar). Every href below is unique; duplicates were
 * collapsed to one canonical entry per the HLD §6 mapping table.
 *
 * Items whose module has shipped on the Go API route to the real /app page
 * (`migrated: true`); every item here has shipped, so this stays true
 * throughout — the flag is kept for parity with AppShellLegacy's rendering
 * of not-yet-migrated items (currently none).
 *
 * `permission` is optional and keyed to the usePermission(resource, action)
 * vocabulary seeded in backend/seeds/seed_dev.sql. Items with no permission
 * are visible to all staff (My Timetable, Approvals, Leave Management, …).
 * super_admin holds every permission, so nothing hides for that role.
 *
 * Permission values live in navItemAccess.ts (golden-test source of truth).
 */
import { NAV_ITEMS, navPermission, type NavItemPermission } from './navItemAccess';
import {
  Users,
  Network,
  Calendar,
  FileText,
  Target,
  School,
  BookOpen,
  BookOpenCheck,
  PencilLine,
  Briefcase,
  Settings,
  BarChart3,
  ChevronDown,
  MessageSquare,
  Ticket,
  Truck,
  Route,
  ClipboardCheck,
  ClipboardList,
  Wallet,
  Boxes,
  Library,
  DoorOpen,
  ClipboardEdit,
  ListChecks,
  PenLine,
  Receipt,
  CalendarDays,
  MapPinned,
  ShieldCheck,
  Inbox,
  Clock3,
  CalendarClock,
  ImageIcon,
  GraduationCap,
  type LucideIcon,
} from 'lucide-react';

export { ChevronDown };
export type { NavItemPermission };

export type NavCategory =
  | 'home'
  | 'academics'
  | 'students_admissions'
  | 'attendance_leave'
  | 'fees'
  | 'operations'
  | 'transportation'
  | 'crm'
  | 'parent_relation'
  | 'people_payroll'
  | 'administration';

export interface ShellNavItem {
  name: string;
  /** Route within the unified shell, e.g. "/app/students". */
  href: string;
  icon: LucideIcon;
  description?: string;
  category: NavCategory;
  /** True once the module ships on the Go API — routes to a real /app page. */
  migrated: boolean;
  /** Hidden when set and usePermission(resource, action) fails for the viewer. */
  permission?: NavItemPermission;
  /**
   * Extra path prefixes (same exact-or-startsWith-slash semantics as `href`)
   * that should also count as "this item is active" for sidebar highlight
   * and breadcrumb purposes. Needed for sibling subpages that don't share
   * this item's own href as a prefix — e.g. Transport Setup's management
   * pages live at /app/transport/{routes,stops,fleet,cost-setup}, not all
   * under /app/transport/routes, so the default href-prefix match alone
   * would miss them and let the /app/transport catch-all ("Fleet Live
   * View") claim them instead.
   */
  activeMatch?: string[];
}

// Order here is the sidebar's group order (Home first, Administration last).
export const categoryConfig: Record<NavCategory, { name: string; color: string }> = {
  home: { name: 'Home', color: 'text-blue-600 bg-blue-50' },
  academics: { name: 'Academics', color: 'text-indigo-600 bg-indigo-50' },
  students_admissions: { name: 'Students & Admissions', color: 'text-emerald-600 bg-emerald-50' },
  attendance_leave: { name: 'Attendance & Leave', color: 'text-cyan-600 bg-cyan-50' },
  fees: { name: 'Fees', color: 'text-teal-600 bg-teal-50' },
  operations: { name: 'Operations', color: 'text-amber-600 bg-amber-50' },
  transportation: { name: 'Transportation', color: 'text-sky-600 bg-sky-50' },
  crm: { name: 'CRM', color: 'text-purple-600 bg-purple-50' },
  parent_relation: { name: 'Parent Relation', color: 'text-pink-600 bg-pink-50' },
  people_payroll: { name: 'People & Payroll', color: 'text-orange-600 bg-orange-50' },
  administration: { name: 'Administration', color: 'text-gray-600 bg-gray-50' },
};

export const navigationItems: ShellNavItem[] = [
  // ---- Home -----------------------------------------------------------------
  // Absorbs the legacy "Analytics Dashboard" / "Analytics" / "School Overview"
  // trio (all three pointed at /app/analytics) into one canonical "Overview".
  {
    name: 'Overview',
    href: '/app/analytics',
    icon: BarChart3,
    description: 'Composed dashboard: attendance, fees, admissions, inventory, library, exams, transport',
    category: 'home',
    migrated: true,
    permission: navPermission('Overview'),
  },
  {
    name: 'Approvals',
    href: '/app/approvals',
    icon: Inbox,
    description: 'Everything routed to you for a decision, plus approval delegations',
    category: 'home',
    migrated: true,
  },
  {
    name: 'My Timetable',
    href: '/app/my-timetable',
    icon: CalendarDays,
    description: 'Your own weekly teaching schedule',
    category: 'home',
    migrated: true,
  },
  {
    // No permission gate: backend gates GET /me/profile on actor.IsStaff()
    // only, and no single existing permission is granted across the full
    // set of roles this page serves — same "visible to all staff" precedent
    // as My Timetable/Approvals/Leave Management above (see file header).
    name: 'My Profile',
    href: '/app/my-profile',
    icon: Users,
    description: 'Your own staff profile: documents, salary structure, and bank details',
    category: 'home',
    migrated: true,
  },

  // ---- CRM & Communication --------------------------------------------------------
  // "Sales & CRM" and "PR Management" dissolve into this one group. "Call
  // Logs" and "Sales Pipeline" (both pointed at /app/leads) are dropped —
  // "Leads" below is the single canonical entry for that href.
  {
    name: 'Leads Dashboard',
    href: '/app/leads/dashboard',
    icon: Target,
    description: 'Lead conversion and sales metrics',
    category: 'crm',
    permission: navPermission('Leads Dashboard'),
    migrated: true,
  },
  {
    name: 'Leads',
    href: '/app/leads',
    icon: Target,
    description: 'Lead management and admissions pipeline',
    category: 'crm',
    permission: navPermission('Leads'),
    migrated: true,
  },
  {
    name: 'Appointments',
    href: '/app/leads/appointments',
    icon: Calendar,
    description: 'Scheduled meetings and visits',
    category: 'crm',
    permission: navPermission('Appointments'),
    migrated: true,
  },
  {
    name: 'Leads Tasks',
    href: '/app/leads/tasks',
    icon: FileText,
    description: 'Follow-ups and to-dos linked to leads',
    category: 'crm',
    permission: navPermission('Leads Tasks'),
    migrated: true,
  },
  {
    name: 'Reports',
    href: '/app/leads/reports',
    icon: BarChart3,
    description: 'Sales performance reports and analytics',
    category: 'crm',
    permission: navPermission('Reports'),
    migrated: true,
  },
  {
    // Emoji dropped from the label per ux-overhaul-hld.md §0 "No emoji in nav labels".
    name: 'WhatsApp (WATI)',
    href: '/app/leads/wati',
    icon: MessageSquare,
    description: 'Configure WATI WhatsApp integration and templates',
    category: 'crm',
    permission: navPermission('WhatsApp (WATI)'),
    migrated: true,
  },
  {
    name: 'CRM Settings',
    href: '/app/leads/config',
    icon: Settings,
    description: 'Sales configuration and settings',
    category: 'crm',
    permission: navPermission('CRM Settings'),
    migrated: true,
  },
  // ---- Academics --------------------------------------------------------------
  {
    name: 'Academic Structure',
    href: '/app/academic',
    icon: BookOpen,
    description: 'Years, terms, classes, sections, subjects, rooms, bell schedule, and the teacher matrix',
    category: 'academics',
    migrated: true,
    permission: navPermission('Academic Structure'),
  },
  {
    name: 'Academic Calendar',
    href: '/app/calendar',
    icon: CalendarDays,
    description: 'Holidays, events, PTMs, and exam dates in one unified calendar',
    category: 'academics',
    migrated: true,
    permission: navPermission('Academic Calendar'),
  },
  {
    name: 'Exams',
    href: '/app/exams',
    icon: ClipboardCheck,
    description: 'Grading config, schedule builder, seating, marks, hall tickets',
    category: 'academics',
    migrated: true,
    permission: navPermission('Exams'),
  },
  {
    name: 'Lesson Plans',
    href: '/app/lesson-plans',
    icon: PenLine,
    description: 'Weekly lesson plan submission, HOD review queue, and approvals',
    category: 'academics',
    migrated: true,
    permission: navPermission('Lesson Plans'),
  },
  {
    name: 'Homework',
    href: '/app/homework',
    icon: BookOpenCheck,
    description: 'Assign homework per section and subject, with due dates and review status',
    category: 'academics',
    migrated: true,
    permission: navPermission('Homework'),
  },
  {
    name: 'Diary',
    href: '/app/diary',
    icon: PencilLine,
    description: 'Daily classwork diary per section, with parent read receipts',
    category: 'academics',
    migrated: true,
    permission: navPermission('Diary'),
  },
  {
    name: 'Students',
    href: '/app/students',
    icon: Users,
    description: 'Student enrollment, records, and bulk operations',
    category: 'students_admissions',
    migrated: true,
    permission: navPermission('Students'),
  },
  {
    name: 'Teachers',
    href: '/app/teachers',
    icon: GraduationCap,
    description: 'Teacher names, contact, and the classes they teach',
    category: 'students_admissions',
    migrated: true,
    permission: navPermission('Teachers'),
  },
  {
    name: 'Student Roster',
    href: '/app/sis/roster',
    icon: School,
    description: 'School roster with transfer-certificate / ID-card issuance and photo upload',
    category: 'students_admissions',
    migrated: true,
    permission: navPermission('Student Roster'),
  },
  {
    name: 'Admissions',
    href: '/app/admissions',
    icon: ClipboardEdit,
    description: 'Inquiry pipeline, seat capacity, waitlist, and SIS conversion',
    category: 'students_admissions',
    migrated: true,
    permission: navPermission('Admissions'),
  },
  {
    name: 'Year-End Promotion',
    href: '/app/sis/promotion',
    icon: ListChecks,
    description: 'Bulk roll students into the next academic year: preview, resolve, confirm, apply',
    category: 'students_admissions',
    migrated: true,
    permission: navPermission('Year-End Promotion'),
  },
  {
    name: 'Student Leave',
    href: '/app/student-leave',
    icon: FileText,
    description: 'Student leave requests and approvals',
    category: 'students_admissions',
    migrated: true,
    permission: navPermission('Student Leave'),
  },

  // ---- Attendance & Leave -------------------------------------------------------
  // "Attendance Management" and "Staff Attendance Review" (both pointed at
  // /app/attendance/review) collapse into one canonical "Staff Attendance".
  {
    name: 'Student Attendance',
    href: '/app/attendance/register',
    icon: ClipboardList,
    description: 'Section register and per-student month view (read-only)',
    category: 'attendance_leave',
    migrated: true,
    permission: navPermission('Student Attendance'),
  },
  {
    name: 'Staff Attendance',
    href: '/app/attendance/review',
    icon: ShieldCheck,
    description: 'Per-day staff check-in review: selfies, geofence, and liveness evidence',
    category: 'attendance_leave',
    migrated: true,
    permission: navPermission('Staff Attendance'),
  },
  {
    name: 'Regularizations',
    href: '/app/attendance/regularizations',
    icon: Clock3,
    description: 'Request attendance corrections and approve staff regularizations',
    category: 'attendance_leave',
    migrated: true,
  },
  {
    name: 'Leave Management',
    href: '/app/leave',
    icon: CalendarClock,
    description: 'Leave requests, balances, leave-type config, and the approval queue',
    category: 'attendance_leave',
    migrated: true,
  },
  {
    name: 'Geofence Settings',
    href: '/app/attendance/geo-config',
    icon: MapPinned,
    description: 'School pin, geofence radius, and selfie/liveness requirements for staff check-in',
    category: 'attendance_leave',
    migrated: true,
    permission: navPermission('Geofence Settings'),
  },

  // ---- Fees ---------------------------------------------------------------------
  {
    name: 'Fees',
    href: '/app/fees',
    icon: Wallet,
    description: 'Fee heads, structures, installments, discounts, concessions, and generation',
    category: 'fees',
    migrated: true,
    permission: navPermission('Fees'),
  },

  // ---- Operations -----------------------------------------------------------------
  {
    name: 'Inventory',
    href: '/app/inventory',
    icon: Boxes,
    description: 'Catalog, stock movements, requisitions, procurement, audits, and assets',
    category: 'operations',
    migrated: true,
    permission: navPermission('Inventory'),
  },
  {
    name: 'Library',
    href: '/app/library',
    icon: Library,
    description: 'Catalog, circulation desk, renewals, requisitions, and borrower policies',
    category: 'operations',
    migrated: true,
    permission: navPermission('Library'),
  },
  {
    name: 'Gate',
    href: '/app/gate',
    icon: DoorOpen,
    description: 'Visitor log, early checkout gate queue, and pickup-log report',
    category: 'operations',
    migrated: true,
    permission: navPermission('Gate'),
  },
  {
    name: 'Expenses',
    href: '/app/expenses',
    icon: Receipt,
    description: 'General spend, reimbursement claims, department budgets, and reports',
    category: 'operations',
    migrated: true,
    permission: navPermission('Expenses'),
  },
  {
    name: 'Tasks',
    href: '/app/tasks',
    icon: ListChecks,
    description: 'Whole-school task management: create, assign, and track operational tasks',
    category: 'operations',
    migrated: true,
  },
  // ---- Transportation ---------------------------------------------------------
  {
    name: 'Fleet Live View',
    href: '/app/transport',
    icon: Truck,
    description: 'Live vehicle tracking and trip status',
    category: 'transportation',
    migrated: true,
    permission: navPermission('Fleet Live View'),
  },
  {
    name: 'Transport Setup',
    href: '/app/transport/routes',
    icon: Route,
    description: 'Buses, stops, routes, schedules, per-student assignments, and fee setup',
    category: 'transportation',
    migrated: true,
    permission: navPermission('Transport Setup'),
    // Management subpages outside the /routes prefix (AppV2Routes.tsx) — without
    // this they'd fall through to the /app/transport wildcard and wrongly
    // highlight "Fleet Live View". fee-slabs itself now only exists as a
    // <Navigate> redirect to cost-setup (AppV2Routes.tsx), so it's dropped
    // here too.
    activeMatch: ['/app/transport/fleet', '/app/transport/stops', '/app/transport/cost-setup'],
  },

  // ---- Parent Relation --------------------------------------------------------
  // Split out of the old "CRM & Communication" block when CRM moved up to
  // second position; these items were always category: 'parent_relation'.
  {
    name: 'PR Tickets',
    href: '/app/tickets',
    icon: Ticket,
    description: 'Parent relationship management tickets',
    category: 'parent_relation',
    // FINDING-1 (docs/test-plans/evidence/grn_verify/checklist.md): manage
    // holders (director_academic/director_admin/general_manager) have full
    // API access identical to triage holders but were nav-invisible.
    permission: navPermission('PR Tickets'),
    migrated: true,
  },
  {
    name: 'PTM',
    href: '/app/ptm',
    icon: Calendar,
    description: 'Parent-teacher meeting scheduling and events',
    category: 'parent_relation',
    permission: navPermission('PTM'),
    migrated: true,
  },
  {
    // Renamed from "Communication" — says what it does.
    name: 'Announcements',
    href: '/app/communication',
    icon: MessageSquare,
    description: 'Publish announcements to staff and parents (in-app + push)',
    category: 'parent_relation',
    permission: navPermission('Announcements'),
    migrated: true,
  },
  {
    // gallery.upload gates the entire /schools/:schoolID/gallery route group
    // server-side, including reads (router.go) — verified against
    // seeds/seed_dev.sql: granted to school_admin_manager, principal,
    // vice_principal, teacher. Publish is separately gated in-page on
    // gallery.publish (separation-of-duties), not at the nav level.
    name: 'Gallery',
    href: '/app/gallery',
    icon: ImageIcon,
    description: 'Photo and video albums for parents, with a publish gate',
    category: 'parent_relation',
    permission: navPermission('Gallery'),
    migrated: true,
  },

  // ---- People & Payroll -----------------------------------------------------------
  // "Staff Management" and "HR Dashboard" (both pointed at /app/hr) are
  // dropped — this is the single canonical entry.
  {
    name: 'HR & Payroll',
    href: '/app/hr',
    icon: Briefcase,
    description: 'Staff profiles, salary structures, payroll runs, payslips, and staff exit / letters',
    category: 'people_payroll',
    migrated: true,
    permission: navPermission('HR & Payroll'),
  },

  // ---- Administration ---------------------------------------------------------------
  {
    name: 'Users & Roles',
    href: '/app/admin/users',
    icon: Users,
    description: 'Staff onboarding, role assignments, and reporting lines',
    category: 'administration',
    migrated: true,
    permission: navPermission('Users & Roles'),
  },
  {
    name: 'Team / Org Chart',
    href: '/app/team',
    icon: Network,
    description: 'Visual reporting hierarchy across the organization',
    category: 'administration',
    migrated: true,
    permission: navPermission('Team / Org Chart'),
  },
  {
    name: 'Org & School Settings',
    href: '/app/settings',
    icon: Settings,
    description: 'Organization profile, schools, departments, and per-school module settings',
    category: 'administration',
    migrated: true,
    permission: navPermission('Org & School Settings'),
  },
];

if (navigationItems.length !== NAV_ITEMS.length) {
  throw new Error(
    `navConfig/NAV_ITEMS length mismatch: ${navigationItems.length} vs ${NAV_ITEMS.length}`,
  );
}
for (let i = 0; i < NAV_ITEMS.length; i++) {
  if (navigationItems[i].name !== NAV_ITEMS[i].name) {
    throw new Error(
      `navConfig/NAV_ITEMS name mismatch at ${i}: ${navigationItems[i].name} vs ${NAV_ITEMS[i].name}`,
    );
  }
}

// "Holiday Management" (admin dupe of /app/calendar, superseded by Academic
// Calendar above) is intentionally not listed.

export const DEFAULT_EXPANDED_CATEGORIES: NavCategory[] = ['home'];

/** Active-route matching used by both the sidebar and the top-bar breadcrumb. */
export function isNavItemActive(item: ShellNavItem, pathname: string): boolean {
  if (!item.migrated) return false;
  const patterns = [item.href, ...(item.activeMatch ?? [])];
  return patterns.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Finds the nav item that best matches the current route. When multiple
 * items match (nested routes sharing a prefix, e.g. /app/attendance/register
 * vs a hypothetical /app/attendance), the longest href wins so the most
 * specific breadcrumb is shown.
 */
export function findActiveNavItem(pathname: string): ShellNavItem | undefined {
  return navigationItems
    .filter((item) => isNavItemActive(item, pathname))
    .sort((a, b) => b.href.length - a.href.length)[0];
}

export function findActiveCategory(pathname: string): NavCategory | undefined {
  return findActiveNavItem(pathname)?.category;
}
